import {QueryClient, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'
import {APIConfigType, ContextualPrefetchType} from 'interfaces'
import {prefetchConfig} from './prefetch.config'
import {reactQueryFetchFunctionMap, warmUpFunctionMap} from './prefetch.queryfn'
import {checkPermission, usePermissions} from 'rbac'

/**
 * @description A Hook to prefetch data on respective contexts
 * @returns prefetch function
 */
const useContextualPrefetch = () => {
  const queryClient = useQueryClient()
  const {permissions: userPermissions} = usePermissions()
  const prefetch = useCallback(
    ({route, context, payload}: ContextualPrefetchType) => {
      const contextualConfig =
        prefetchConfig.contextual[
          route as keyof typeof prefetchConfig.contextual
        ]
      const config = contextualConfig?.[
        context as keyof typeof contextualConfig
      ] as unknown as APIConfigType[] | undefined

      if (!config) return

      const payloadMap = payload ?? {}
      const reactQueryFetchers =
        reactQueryFetchFunctionMap as unknown as Record<
          string,
          (
            queryClient: QueryClient,
            queryKey?: unknown,
            payload?: unknown,
          ) => Promise<void> | void
        >
      const warmUpFetchers = warmUpFunctionMap as unknown as Record<
        string,
        (payload?: unknown) => Promise<void> | void
      >

      config.forEach((apiConfig: APIConfigType) => {
        const {isReactQuery, reqPermissions} = apiConfig

        const hasPermission = checkPermission(reqPermissions, userPermissions)
        if (!hasPermission) return

        if (isReactQuery) {
          const {queryKey, queryFnName} = apiConfig
          const data = queryClient.getQueryData(queryKey)

          if (!data) {
            const fetchFunction = reactQueryFetchers[queryFnName]
            if (!fetchFunction) return

            void fetchFunction(queryClient, queryKey, payloadMap[queryFnName])
          }
        } else {
          const {id} = apiConfig
          const warmUpFunction = warmUpFetchers[id]
          if (!warmUpFunction) return

          void warmUpFunction(payloadMap[id])
        }
      })
    },
    [queryClient, userPermissions],
  )

  return {prefetch}
}

export default useContextualPrefetch
